import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classes from './SetDateAndTime.module.scss';
import { PostOnDate } from '../PostOnDate';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { mapTimezone } from '../../../../helpers/map-timezone';
import { Select } from '../../../../components/forms/Select';
import { PostEditorValidationMessages } from '../../constants/validation-messages';
import { TimezoneSetting } from '../../../../models/graphql';
import { PostEditorErrors, PostOnDateObject } from '../../../../models/post-editor';
import { FormElementAction } from '../../../../models/basic-objects';
import { ScheduleHelpers } from '../Schedule.helpers';
import { getErrorMessages } from '../../validators/helpers/getErrorMessages';
import { hasSeverity } from '../../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../../constants/form';

export type SetDateAndTimeViewProps = {
  //
  dates: PostOnDateObject[];
  formInstance: UseFormReturn;
  groupName: string;
  loading: boolean;
  timeZone: TimezoneSetting;
  expandTimezoneSelector: boolean;
  selectAction: FormElementAction;
  addNewDateHandler: VoidFunction;
  removeDateHandler: (index: number) => void;
  onUpdate: (date: Date, index: number, complete?: (keepNew?: boolean) => void) => void;
  postGroupErrors: PostEditorErrors;
  customizedProfileId: string | null;
};

export const SetDateAndTimeView = ({
  addNewDateHandler,
  removeDateHandler,
  formInstance,
  groupName,
  dates,
  loading,
  timeZone,
  expandTimezoneSelector,
  selectAction,
  onUpdate,
  postGroupErrors,
  customizedProfileId
}: SetDateAndTimeViewProps): JSX.Element => {
  const [timezoneVisibility, setTimezoneVisibility] = useState<boolean>(expandTimezoneSelector);
  const timeZoneListItems = mapTimezone();
  const timezoneRules: RegisterOptions = {
    required: PostEditorValidationMessages.schedule.timezone.required
  };

  const disabled = useMemo(() => dates.map(currDate => currDate.isPast), [dates]);

  const notDisabledLength = useMemo(() => {
    if (disabled) {
      return disabled.filter(value => !value).length;
    } else {
      return 0;
    }
  }, [disabled]);

  const canDelete = useCallback(
    (index: number) => {
      if (disabled) {
        return notDisabledLength > 1 && !disabled[index];
      } else {
        return dates.length > 1;
      }
    },
    [disabled, dates, notDisabledLength]
  );

  const errors = useMemo(
    () =>
      dates.map((_, index) => {
        const isError = hasSeverity(postGroupErrors, ErrorMessageSeverity.error, customizedProfileId, index + 1, [
          'checkDateAndTime'
        ]);
        return isError
          ? getErrorMessages(postGroupErrors, customizedProfileId, index + 1, ['checkDateAndTime']).join('\n')
          : '';
      }),
    [dates, postGroupErrors, customizedProfileId]
  );

  const warnings = useMemo(
    () =>
      dates.map((_, index) => {
        const isWarning = hasSeverity(
          postGroupErrors,
          ErrorMessageSeverity.warning,
          customizedProfileId,
          index + 1,
          []
        );
        return isWarning ? getErrorMessages(postGroupErrors, customizedProfileId, index + 1, []).join('\n') : '';
      }),
    [dates, postGroupErrors, customizedProfileId]
  );

  useEffect(() => {
    setTimezoneVisibility(expandTimezoneSelector);
  }, [expandTimezoneSelector]);

  return (
    <>
      {dates.map((date, index) => {
        const { date: postOnDate, time, isPast } = date;
        // const errors = getErrors(index);
        return (
          <PostOnDate
            key={`${ScheduleHelpers.convertPostOnDateObjectToIsoString(date)}-${index}`}
            date={postOnDate}
            time={time}
            onUpdate={(date, complete) => onUpdate(date, index, complete)}
            disabled={isPast || loading}
            name={`${groupName}.${index}`}
            formInstance={formInstance}
            {...(errors[index].length ? { error: errors[index] } : {})}
            {...(warnings[index].length ? { warning: warnings[index] } : {})}
            {...(canDelete(index) ? { action: () => removeDateHandler(index) } : {})}
            {...(isPast !== undefined ? { isPast } : {})}
            wrapper
          />
        );
      })}
      <div className={'grid-x margin-top-small-24'}>
        <div className={'cell shrink'}>
          <a onClick={() => addNewDateHandler()}>Add another date</a>
        </div>
        <div className={`cell shrink ${classes.ChangeTimezone}`}>
          <a onClick={() => setTimezoneVisibility(old => !old)}>
            {timezoneVisibility ? 'Hide Timezone' : 'Change Timezone'}
          </a>
        </div>
        {timezoneVisibility && (
          <div className={'cell small-12 margin-top-small-16'}>
            <Select
              disabled={loading}
              items={timeZoneListItems}
              action={selectAction}
              selectedValue={timeZone}
              label={'Timezone'}
              name={`${groupName}Timezone`}
              size={'large'}
              rules={timezoneRules}
              useFormInstance={formInstance}
            />
          </div>
        )}
      </div>
    </>
  );
};
